import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react'
import { v4 as uuid } from 'uuid'

import { decodeToken } from '@/utils/jwt'

import { omsApi } from 'oddle-shop-monorepo.services/oms/base-api'
import { SuccessApiResponse } from 'next-api-handler'

const staggeredBaseQueryWithBailOut = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl: '/api',
      prepareHeaders: (headers, { getState }) => {
        const accessToken = window.localStorage.getItem('accessToken')
        const { clientId } = decodeToken(accessToken || '')

        headers.set('Content-Type', 'application/json')
        headers.set('Accept', 'application/json')
        headers.set('X-Request-Id', uuid())
        if (accessToken) {
          headers.set('X-Oddle-Pass-Token', accessToken)
          headers.set('X-Oddle-Pass-Client-Id', clientId)
        }

        const cookies = document.cookie.split(';')
        cookies.forEach((cookie) => {
          const [name, value] = cookie.trim().split('=')
          if (name && value) {
            headers.set('Cookie', `${name}=${value}`)
          }
        })

        return headers
      },
      timeout: 10000,
    })(args, api, extraOptions)

    if (
      result.error &&
      typeof result.error.status === 'number' &&
      result.error.status < 500 &&
      result.error.status >= 400
    ) {
      retry.fail(result.error)
    }

    return result
  },
  { maxRetries: 1 }
)

export const localApi = createApi({
  reducerPath: 'local',
  baseQuery: staggeredBaseQueryWithBailOut,
  tagTypes: [],
  endpoints: () => ({}),
})

export const injectedLocalApi = localApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    retrieveCustomerInfo: builder.query<
      Oms.V4ApiResponse<{
        payload: Oms.UserData
        errorCode?: string
        errorMessage?: string
      }>,
      { clientId?: string; cartName?: string | null }
    >({
      query: ({ clientId, cartName }) => ({
        url: '/v2/user',
        params: { subdomain: cartName, clientId },
      }),
    }),
    getTheme: builder.query({
      query: ({
        menuId,
        previewToken,
      }: {
        menuId?: string
        previewToken?: string | null
      }) => ({
        url: '/v2/theme',
        params: { siteId: menuId, previewToken, menuId },
      }),
    }),
    getPageData: builder.query({
      query: ({
        menuId,
        previewToken,
        pageName,
      }: {
        menuId?: string
        previewToken?: string | null
        pageName: string
      }) => ({
        url: '/v2/page-data',
        params: { siteId: menuId, previewToken, pageName },
      }),
    }),
    getStoreOrder: builder.query<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder }>,
      string | undefined
    >({
      query: (storeOrderId) => ({
        url: '/v2/order',
        method: 'POST',
        body: {
          storeOrderId,
        },
      }),
    }),
    syncOrderMethod: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder; error?: string }>,
      { storeOrderId: string; method: string }
    >({
      query: ({ storeOrderId, method }) => ({
        url: '/v2/order-method',
        method: 'POST',
        body: {
          storeOrderId,
          method,
        },
      }),
    }),
    updateDeliveryLocation: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder; error?: string }>,
      { address: Oms.UpdateDeliveryLocationPayload; storeOrderId: string }
    >({
      query: ({ storeOrderId, address }) => ({
        url: '/v2/delivery-location',
        method: 'POST',
        body: {
          address,
          storeOrderId,
        },
      }),
    }),
    updateDeliveryAddressTwo: builder.mutation<
      Local.ApiResponse<{
        address: string
        addressTwo: string
      }>,
      { addressTwo: string; storeOrderId: string }
    >({
      query: ({ storeOrderId, addressTwo }) => ({
        url: '/v2/delivery-address-two',
        method: 'POST',
        body: {
          storeOrderId,
          addressTwo,
        },
      }),
    }),
    updatePickupLocation: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder }>,
      {
        storeId: string
        storeOrderId: string
        lat?: string
        long?: string
      }
    >({
      query: ({ storeOrderId, storeId, lat, long }) => ({
        url: '/v2/pickup-location',
        method: 'POST',
        body: { storeOrderId, storeId, lat, long },
      }),
    }),
    updateCollectionDateTime: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder }>,
      {
        storeOrderId: string
        collectionDate?: string
        collectionTimeSlot: string
        collectionTimeSlotId: string
      }
    >({
      query: ({
        storeOrderId,
        collectionDate,
        collectionTimeSlot,
        collectionTimeSlotId,
      }) => ({
        url: '/v2/collection-datetime',
        method: 'POST',
        body: {
          storeOrderId,
          collectionDate,
          collectionTimeSlot,
          collectionTimeSlotId,
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const response = await queryFulfilled
          if (response?.data?.data?.payload?.storeOrder) {
            dispatch(omsApi.util.invalidateTags(['MenuItems']))
          }
        } catch {
          return
        }
      },
    }),
    setDefaultBillingDeliveryAddress: builder.mutation<
      Local.ApiResponse<Oms.StoreOrder>,
      {
        addressId: string
        customerId: string
        menuId: string
      }
    >({
      query: ({ addressId, customerId, menuId }) => ({
        url: '/v2/default-address',
        method: 'POST',
        body: { addressId, customerId, menuId },
      }),
    }),
    addItemToCart: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder; error?: string }>,
      {
        storeOrderId: string
        orderItem: {
          variantId: string
          quantity: number
          subitems: Record<string, unknown>[]
        }
      }
    >({
      query: ({ storeOrderId, orderItem }) => ({
        url: '/v2/add-item-to-cart',
        method: 'POST',
        body: { storeOrderId, orderItem },
      }),
    }),
    setPrepareCheckout: builder.mutation<
      Local.ApiResponse<{
        storeOrder: Oms.StoreOrder
        result: boolean
        errorCodes?: string[]
        errors?: string[]
      }>,
      string
    >({
      query: (storeOrderId) => ({
        url: '/v2/prepare-checkout',
        method: 'POST',
        body: { storeOrderId },
      }),
    }),
    getStoreOrderMetadata: builder.query<
      Local.V4ApiResponse<{ segment: Record<string, unknown> }>,
      string
    >({
      query: (storeOrderId) => ({
        url: '/v2/store-order-metadata',
        params: { storeOrderId },
      }),
    }),
    updateStoreOrderItemQuantity: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder }>,
      { storeOrderId: string; orderItems: Oms.OrderItem[] }
    >({
      query: ({ storeOrderId, orderItems }) => ({
        url: '/v2/update-order-item-quantity',
        method: 'POST',
        body: {
          storeOrderId,
          items: orderItems,
        },
      }),
    }),
    clearCart: builder.mutation<
      Local.ApiResponse<{ storeOrder: Oms.StoreOrder }>,
      string
    >({
      query: (storeOrderId) => ({
        url: '/v2/clear-cart',
        method: 'DELETE',
        params: {
          storeOrderId,
        },
      }),
    }),
    applyPromotions: builder.mutation<
      Local.ApiResponse<{
        storeOrder: Oms.StoreOrder
        failedPromotions: (SelectedPromotion & { error: string })[]
        error: string
      }>,
      {
        promotions: {
          promotionId: string
          variants: {
            variantId: string
            subitems: Record<string, unknown>[]
            quantity: number
          }[]
        }[]
        storeOrderId: string
      }
    >({
      query: ({ storeOrderId, promotions }) => ({
        url: '/v2/apply-promotions',
        method: 'POST',
        body: {
          storeOrderId,
          promotions,
        },
      }),
    }),
    getCheckoutMeta: builder.query<
      Local.V4ApiResponse<Oms.CheckoutMetaData>,
      string
    >({
      query: (storeOrderId) => ({
        url: '/v2/checkout-meta',
        method: 'GET',
        params: {
          storeOrderId,
        },
      }),
    }),
    getInvoiceMeta: builder.query<
      Local.ApiResponse<{
        storeOrder: Oms.CheckoutMetaData
      }>,
      { storeOrderId: string; hashCode?: string }
    >({
      query: ({ storeOrderId, hashCode }) => ({
        url: '/v2/invoice-meta',
        method: 'GET',
        params: {
          storeOrderId,
          hashCode,
        },
      }),
    }),
    signupCustomer: builder.mutation<
      Local.ApiResponse<{
        facebookId?: string
      }>,
      {
        email?: string
        firstName?: string
        lastName?: string
        password?: string
        phone?: string
        storeOrderId?: string
        menuId?: string
        facebookId?: string
        source?: string
        gender?: string
        dob?: string
        hash?: string
        receiveEmail?: string
        acceptMarketing?: string
        acceptMarketingMenu?: string
        subdomain?: string
      }
    >({
      query: (payload) => ({
        url: '/v2/user',
        method: 'POST',
        body: payload,
      }),
    }),
    getAllPromotionsFilmstrip: builder.query<
      { data: { payload: { promotions: Promotion[] } } },
      {
        storeOrderId?: string
        mode?: string
      }
    >({
      query: ({ storeOrderId, mode }) => ({
        url: `/v2/promotions-filmstrip`,
        method: 'GET',
        params: {
          storeOrderId,
          mode,
        },
      }),
    }),
    signupNewsletter: builder.mutation<
      Local.V4ApiResponse<string>,
      {
        email?: string
        firstName?: string
        lastName?: string
        menuId?: string
      }
    >({
      query: (payload) => ({
        url: '/v2/signup-newsletter',
        method: 'POST',
        body: payload,
      }),
    }),
    checkPromotionCode: builder.query<
      Oms.V4ApiResponse<{
        payload: { promotion: SelectedPromotion; error: string }
      }>,
      {
        storeOrderId?: string
        code?: string
      }
    >({
      query: ({ storeOrderId, code }) => ({
        url: '/v2/get-promo-code',
        params: {
          storeOrderId,
          code,
        },
      }),
    }),
    getAddressBook: builder.query<
      Oms.V4ApiResponse<{ data: Oms.DeliveryAddresses[] }>,
      undefined
    >({
      query: () => ({
        url: '/v2/address-book',
      }),
    }),
    updateUserInfo: builder.mutation<
      Local.ApiResponse<{ error?: string }>,
      {
        customerId: string
        menuId?: string | null
        email: string
        firstName: string
        lastName: string
        organisationName?: string | null
        phone: string
        receiveEmail?: boolean
        additionalPhone?: string | null
        salutation: string
        source?: string
        gender?: string | null
        dob?: string | null
        acceptMarketing: boolean
        acceptMarketingMenu: boolean
      }
    >({
      query: (payload) => ({
        url: '/v2/user',
        method: 'PUT',
        body: payload,
      }),
    }),
    updateCheckoutMeta: builder.mutation<
      Local.V4ApiResponse<Oms.CheckoutMetaData>,
      {
        storeOrderId: string
        payload: {
          recipientContact: string
        }
      }
    >({
      query: ({ storeOrderId, payload }) => ({
        url: `/v2/checkout-meta?storeOrderId=${storeOrderId}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    getAddressFormat: builder.query<
      Oms.V4ApiResponse<{ data: Oms.AddressFormat }>,
      string | undefined
    >({
      query: (menuId) => ({
        url: `/v2/address-format?menuId=${menuId}`,
      }),
    }),
    updateDeliveryDetails: builder.mutation<
      Local.V4ApiResponse<Oms.StoreOrder>,
      {
        storeOrderId: string
        deliveryAddressType?: string
        deliveryAddressTwoDetails?: Record<string, string>
        deliveryInstructions?: string
      }
    >({
      query: (body) => ({
        url: `/v2/update-delivery-details`,
        method: 'PUT',
        body,
      }),
    }),
    getPaymentOptions: builder.query<
      Oms.V4ApiResponse<{ payload: { paymentOptions: Oms.PaymentMethod[] } }>,
      {
        storeOrderId?: string
        enabledStripePaymentElement: boolean
      }
    >({
      query: ({ storeOrderId }) => ({
        url: `/v2/payment-options?storeOrderId=${storeOrderId}`,
      }),
    }),
    postStripe3DsCharges: builder.mutation<
      Local.V4ApiResponse<Oms.CheckoutMetaData>,
      {
        storeOrderId?: string
        sourceId?: string | null
      }
    >({
      query: (body) => ({
        url: `/v2/stripe-3ds-charges`,
        method: 'POST',
        body,
      }),
    }),
    removeSelectedVoucher: builder.mutation<
      Local.V4ApiResponse<string>,
      {
        storeOrderId?: string
        voucherId?: string
      }
    >({
      query: ({ voucherId, storeOrderId }) => ({
        url: `/v2/remove-selected-voucher?voucherId=${voucherId}&storeOrderId=${storeOrderId}`,
        method: 'DELETE',
      }),
    }),
    getVouchers: builder.query<
      Local.ApiResponse<Oms.Vouchers[]>,
      Oms.VoucherValidationPayload
    >({
      query: (body) => ({
        url: '/v2/get-vouchers',
        method: 'POST',
        body,
      }),
    }),
    validateVoucher: builder.mutation<
      {
        error?: any
        data?: Oms.SelectedVoucher
      },
      {
        customer: {
          source_id: string
          email?: string
          [key: string]: any
        }
        order: {
          amount: number
          items?: any[]
          [key: string]: any
        }
        code: string
      }
    >({
      query: (body) => ({
        url: '/v2/validate-voucher',
        method: 'POST',
        body,
      }),
    }),
    getAllSavedCards: builder.query<User.UserSavedCreditCard, void>({
      queryFn: async () => {
        try {
          const result = await window.opw.getAllSavedCards()
          return { data: result.data }
        } catch {
          return {
            data: {} as User.UserSavedCreditCard,
          }
        }
      },
    }),
    setCardDefault: builder.mutation<User.UserSavedCreditCard, string>({
      queryFn: async (paymentMethodId) => {
        try {
          const result = await window.opw.setCardDefault(paymentMethodId)
          return { data: result.data }
        } catch {
          return {
            data: {} as User.UserSavedCreditCard,
          }
        }
      },
    }),
    updateUserDeliveryAddress: builder.mutation<
      void,
      {
        customerId: string
        id: string
        autoCompleteAddress: string
        addressTwo: string
        city: string
        state: string
        country: string
        postal: string
        latitude: string
        longitude: string
      }
    >({
      queryFn: async ({
        customerId,
        id,
        autoCompleteAddress,
        addressTwo,
        city,
        state,
        country,
        postal,
        latitude,
        longitude,
      }) => {
        try {
          const result = await window.opw.updateAddress(id, {
            id,
            autoCompleteAddress,
            addressTwo,
            city,
            state,
            country,
            postal,
            latitude,
            longitude,
            customerId,
          })
          return { data: result.data }
        } catch {
          return {
            data: {} as User.UserSavedCreditCard,
          }
        }
      },
    }),
    submitCheckout: builder.mutation<
      Oms.V4ApiResponse<{
        data: Oms.CheckoutResultData
      }>,
      {
        storeOrderId: string
        customerId: string
        customerEmail: string
        customerFirstName: string
        customerLastName: string
        customerContactNumber?: string
        customerCompany: string
        recipientName: string
        recipientContact?: string
        billingAddressOne?: string
        billingAddressTwo?: string
        billingAddressState?: string
        billingAddressCity?: string
        billingAddressPostal?: string
        billingAddressCountry?: string
        additionalInformation?: string | null
        organisationName?: string
        stripeRequestPaymentButtonType?: string
        paymentOption?: string | null
        paymentOptionId?: string | null
        paymentType?: number
        subscribeToEmail?: boolean
        unifiedBusinessNumber: string
        carrierType?: string
        carrierNumber?: string
        paymentToken?: string
        redirectUrl?: string
        otp: string | null
        version: string
        voucher?: Record<string, unknown>
        saveForFuture?: boolean
        existing?: boolean
      }
    >({
      query: (body) => ({
        url: '/v2/submit-checkout',
        method: 'POST',
        body,
      }),
    }),
    getStripeToken: builder.query<
      Local.V4ApiResponse<{ stripeToken: string }>,
      { menuId?: string; testAccount?: boolean }
    >({
      query: ({ menuId, testAccount }) => ({
        url: '/v2/get-stripe-token',
        method: 'GET',
        params: {
          menuId,
          ...(testAccount ? { testAccount } : {}),
        },
      }),
    }),
    getCtaConfig: builder.query<{ data: Record<string, string> }, undefined>({
      query: () => ({
        url: '/v2/get-cta-config',
      }),
    }),
    getMaxDaysPickerConfig: builder.query<
      { data: Record<string, number> },
      string
    >({
      query: () => ({
        url: '/v2/get-max-days-picker-config',
      }),
    }),
    getTrackDelivery: builder.query<
      Local.V4ApiResponse<{ trackingLink: string; etaToCustomer: number }>,
      string | undefined
    >({
      query: (storeOrderId) => ({
        url: '/v2/get-track-delivery',
        method: 'GET',
        params: {
          storeOrderId,
        },
      }),
    }),
    verifyAuthenticateToken: builder.query<
      Oms.V4ApiResponse<{
        data: {
          success: boolean
        }
      }>,
      {
        storeOrderId?: string
        token?: string | null
      }
    >({
      query: (body) => ({
        url: '/v2/verify-authenticate-token',
        method: 'POST',
        body,
      }),
    }),
    getOEBanner: builder.query<
      Oms.V4ApiResponse<{
        data: {
          oddleShopMarketingBanners: {
            androidLink: string
            iosLink: string
            mobileImage: {
              url: string
            }
            desktopImage: {
              url: string
            }
          }[]
        }
      }>,
      string | undefined
    >({
      query: (countryCode) => ({
        url: '/v2/get-oe-banner',
        method: 'GET',
        params: {
          countryCode,
        },
      }),
    }),
    getStoreOrderPaymentStatus: builder.query<
      Local.ApiResponse<Oms.StoreOrderAndPaymentStatus>,
      string | undefined
    >({
      query: (storeOrderId) => ({
        url: '/v2/get-store-order-and-payment-status',
        method: 'GET',
        params: {
          storeOrderId,
        },
      }),
    }),
    getMenusSupportEInvoice: builder.query<
      { data: boolean },
      string | undefined
    >({
      query: (menuId) => ({
        url: '/v2/get-menus-support-einvoice',
        method: 'GET',
        params: {
          menuId,
        },
      }),
      transformResponse: (
        response: SuccessApiResponse<Record<string, boolean>>,
        meta,
        menuId
      ): { data: boolean } => {
        return {
          ...response,
          data: Boolean(response.data[menuId as string]),
        }
      },
    }),
    getReservationChannel: builder.query<
      Oms.V4ApiResponse<{ data: { reserve: Reservation.ReservationsInfo } }>,
      string | undefined
    >({
      query: (menuId) => ({
        url: '/v2/get-reserve-channel',
        method: 'GET',
        params: {
          menuId,
        },
      }),
    }),
    getReservationBrandInfo: builder.query<
      Oms.V4ApiResponse<Reservation.BrandInfo>,
      string | undefined
    >({
      query: (shortenBrandName) => ({
        url: '/v2/get-brand-info',
        method: 'GET',
        params: {
          shortenBrandName,
        },
      }),
    }),
    getReservationAvailableDate: builder.query<
      Oms.V4ApiResponse<Reservation.OutletDate[]>,
      { outletId?: string; pax?: number; month?: string }
    >({
      query: ({ outletId, pax, month }) => ({
        url: '/v2/get-available-date',
        method: 'GET',
        params: { outletId, pax, month },
      }),
    }),
    getReservationTimeslot: builder.query<
      Oms.V4ApiResponse<
        {
          availableTimeSlots: string[]
        }[]
      >,
      { outletId?: string; pax?: number; date?: string }
    >({
      query: ({ outletId, pax, date }) => ({
        url: '/v2/get-timeslot',
        method: 'GET',
        params: { outletId, pax, date },
      }),
    }),
    getOPAccessToken: builder.query<
      Oms.V4ApiResponse<{
        data: {
          accessToken: string
        }
      }>,
      { menuId?: string; customerId?: string }
    >({
      query: ({ menuId, customerId }) => ({
        url: '/v2/get-op-access-token',
        method: 'GET',
        params: { menuId, customerId },
      }),
    }),
    getOnlinePaymentOptions: builder.query<
      Oms.V4ApiResponse<{ payload: { paymentOptions: Oms.PaymentMethod[] } }>,
      {
        storeOrderId?: string
        enabledStripePaymentElement: boolean
      }
    >({
      query: ({ storeOrderId }) => ({
        url: `/v2/online-payment-options?storeOrderId=${storeOrderId}`,
      }),
    }),
  }),
})

export const {
  useRetrieveCustomerInfoQuery,
  useLazyRetrieveCustomerInfoQuery,
  useLazyGetThemeQuery,
  useLazyGetPageDataQuery,
  useGetStoreOrderQuery,
  useSyncOrderMethodMutation,
  useUpdateDeliveryLocationMutation,
  useUpdateDeliveryAddressTwoMutation,
  useUpdatePickupLocationMutation,
  useUpdateCollectionDateTimeMutation,
  useSetDefaultBillingDeliveryAddressMutation,
  useAddItemToCartMutation,
  useSetPrepareCheckoutMutation,
  useUpdateStoreOrderItemQuantityMutation,
  useClearCartMutation,
  useApplyPromotionsMutation,
  useLazyGetCheckoutMetaQuery,
  useLazyGetInvoiceMetaQuery,
  useSignupCustomerMutation,
  useGetAllPromotionsFilmstripQuery,
  useSignupNewsletterMutation,
  useLazyCheckPromotionCodeQuery,
  useGetAddressBookQuery,
  useUpdateUserInfoMutation,
  useUpdateCheckoutMetaMutation,
  useGetAddressFormatQuery,
  useUpdateDeliveryDetailsMutation,
  useGetPaymentOptionsQuery,
  usePostStripe3DsChargesMutation,
  useRemoveSelectedVoucherMutation,
  useGetVouchersQuery,
  useValidateVoucherMutation,
  useGetAllSavedCardsQuery,
  useSetCardDefaultMutation,
  useUpdateUserDeliveryAddressMutation,
  useSubmitCheckoutMutation,
  useGetStripeTokenQuery,
  useGetCtaConfigQuery,
  useGetMaxDaysPickerConfigQuery,
  useGetTrackDeliveryQuery,
  useLazyGetTrackDeliveryQuery,
  useVerifyAuthenticateTokenQuery,
  useGetOEBannerQuery,
  useGetStoreOrderPaymentStatusQuery,
  useLazyGetStoreOrderPaymentStatusQuery,
  useGetMenusSupportEInvoiceQuery,
  useLazyGetStoreOrderMetadataQuery,
  useGetReservationChannelQuery,
  useGetReservationBrandInfoQuery,
  useGetReservationAvailableDateQuery,
  useGetReservationTimeslotQuery,
  useGetOPAccessTokenQuery,
  useGetOnlinePaymentOptionsQuery,
} = injectedLocalApi
